import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserBackendService } from '../../backend-services/user/user.backend-service';
import { AuthService } from '../auth/auth.service';
import { CardDisplayType } from '../../types';
import { DEFAULT_BOT_SPEED, isBotSpeed, SettingsInterface } from '../../interfaces';
import { SoundSettingsService } from '../sound-settings/sound-settings.service';
import { CardDisplayTypeService } from 'src/app/modules/game/services/card-display-type.service';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private _settings$ = new BehaviorSubject<SettingsInterface>(
    this.getDefaultSettings()
  );
  private _initialized = false;

  constructor(
    private userBackendService: UserBackendService,
    private authService: AuthService
  ) {}

  public async init(): Promise<void> {
    if (this._initialized) return;

    await this.loadSettings();
    this._initialized = true;
  }

  public get settings$(): Observable<SettingsInterface> {
    return this._settings$.asObservable();
  }

  public get currentSettings(): SettingsInterface {
    return this._settings$.value;
  }

  public async updateSetting<K extends keyof SettingsInterface>(
    key: K,
    value: SettingsInterface[K]
  ): Promise<void> {
    const currentSettings = this._settings$.value;
    const updatedSettings = { ...currentSettings, [key]: value };

    this._settings$.next(updatedSettings);

    await this.saveSettings(updatedSettings);
  }

  public async updateSettings(
    settings: Partial<SettingsInterface>
  ): Promise<void> {
    const currentSettings = this._settings$.value;
    const updatedSettings = { ...currentSettings, ...settings };

    this._settings$.next(updatedSettings);

    await this.saveSettings(updatedSettings);
  }

  private async loadSettings(): Promise<void> {
    try {
      if (await this.authService.isAuthenticated()) {
        const backendSettings = await this.userBackendService.getSettings();

        console.log('Loaded settings from backend', backendSettings);

        if (backendSettings?.settings) {
          const settings = this.mapBackendSettingsToUserSettings(
            backendSettings.settings
          );
          this._settings$.next(settings);
        } else {
          const localSettings = this.getSettingsFromLocalStorage();
          this._settings$.next(localSettings);

          await this.saveSettings(localSettings);
        }
      } else {
        const localSettings = this.getSettingsFromLocalStorage();
        this._settings$.next(localSettings);
      }
    } catch (error) {
      console.error(
        'Failed to load settings from backend, using localStorage:',
        error
      );
      const localSettings = this.getSettingsFromLocalStorage();
      this._settings$.next(localSettings);
    }
  }

  private async saveSettings(settings: SettingsInterface): Promise<void> {
    this.saveSettingsToLocalStorage(settings);

    if (await this.authService.isAuthenticated()) {
      try {
        const backendSettings = this.mapUserSettingsToBackend(settings);
        await this.userBackendService.updateSettings(backendSettings);
      } catch (error) {
        console.error('Failed to save settings to backend:', error);
      }
    }
  }

  private getSettingsFromLocalStorage(): SettingsInterface {
    const cardDisplayType =
      (localStorage.getItem('cardDisplayType') as CardDisplayType) ||
      'doppeldeutsch';
    const sfxSoundsEnabled =
      localStorage.getItem('sfxSoundsEnabled') === 'true';
    const speechSoundsEnabled =
      localStorage.getItem('speechSoundsEnabled') === 'true';
    const keepChatFocused = localStorage.getItem('keepChatFocused') === 'true';
    const botSpeed = localStorage.getItem('botSpeed');

    return SettingsInterface.builder()
      .cardDisplayType(
        ['doppeldeutsch', 'französisch', 'alpinetricks'].includes(
          cardDisplayType
        )
          ? cardDisplayType
          : 'doppeldeutsch'
      )
      .sfxSoundsEnabled(sfxSoundsEnabled)
      .speechSoundsEnabled(speechSoundsEnabled)
      .keepChatFocused(keepChatFocused)
      .botSpeed(isBotSpeed(botSpeed) ? botSpeed : DEFAULT_BOT_SPEED)
      .build();
  }

  private saveSettingsToLocalStorage(settings: SettingsInterface): void {
    localStorage.setItem('cardDisplayType', settings.cardDisplayType);
    localStorage.setItem(
      'sfxSoundsEnabled',
      settings.sfxSoundsEnabled.toString()
    );
    localStorage.setItem(
      'speechSoundsEnabled',
      settings.speechSoundsEnabled.toString()
    );
    localStorage.setItem(
      'keepChatFocused',
      settings.keepChatFocused.toString()
    );
    localStorage.setItem('botSpeed', settings.botSpeed);
  }

  private mapBackendSettingsToUserSettings(
    backendSettings: any
  ): SettingsInterface {
    return SettingsInterface.builder()
      .cardDisplayType(backendSettings.cardDisplayType || 'doppeldeutsch')
      .sfxSoundsEnabled(backendSettings.sfxEnabled ?? true)
      .speechSoundsEnabled(backendSettings.voicesEnabled ?? true)
      .keepChatFocused(backendSettings.keepChatFocused ?? false)
      .blockGuests(backendSettings.blockGuests ?? false)
      .botSpeed(
        isBotSpeed(backendSettings.botSpeed)
          ? backendSettings.botSpeed
          : DEFAULT_BOT_SPEED
      )
      .build();
  }

  private mapUserSettingsToBackend(settings: SettingsInterface): any {
    return {
      cardDisplayType: settings.cardDisplayType,
      sfxEnabled: settings.sfxSoundsEnabled,
      voicesEnabled: settings.speechSoundsEnabled,
      keepChatFocused: settings.keepChatFocused,
      blockGuests: settings.blockGuests,
      botSpeed: settings.botSpeed,
    };
  }

  private getDefaultSettings(): SettingsInterface {
    return SettingsInterface.builder().build();
  }
}
